import apiClient, { withAuth } from './api';

/**
 * Enhanced circle service for managing user circles and add-ons
 * - Complete AddOn integration with backend APIs
 * - Robust error handling and logging
 * - Full Circle Management Center support
 * - Optimized for CircleTabDetailView component
 * - Fixed all scope and circular reference issues
 * - FIXED: Add-on operations use specific order identifiers
 * - UPDATED: Enhanced getUserOrderForCircle for add-on fix
 */

// ==================== ADD-ON HELPER FUNCTIONS ====================

/**
 * Add an add-on to a user's subscription - FIXED VERSION
 * Now passes the specific stripe_subscription_id to ensure correct order is used
 * @param {string|number} circleId - Circle ID
 * @param {string|number} addonId - Add-on ID to add
 * @param {string} token - Auth token
 * @param {string} stripeSubscriptionId - Specific subscription ID from circle
 * @param {string} orderNumber - Specific order number from circle (fallback)
 * @returns {Promise} - Promise resolving to updated subscription data
 */
const addCircleAddon = (circleId, addonId, token, stripeSubscriptionId = null, orderNumber = null) => {
  console.log(`📡 API: Adding add-on ${addonId} to circle ${circleId} with specific identifiers`);
  console.log(`🔍 Using stripe_subscription_id: ${stripeSubscriptionId}, order_number: ${orderNumber}`);
  
  const requestData = {
    addon_id: addonId,
    action: 'add'
  };
  
  // Include specific order identifiers to ensure backend uses the correct order
  if (stripeSubscriptionId) {
    requestData.stripe_subscription_id = stripeSubscriptionId;
  }
  if (orderNumber) {
    requestData.order_number = orderNumber;
  }
  
  return apiClient.post(`/api/circles/${circleId}/addons/`, requestData, withAuth(token))
  .then(response => {
    console.log(`✅ API: Successfully added add-on ${addonId} to circle ${circleId}`, response);
    return response;
  })
  .catch(error => {
    console.error(`❌ API: Error adding add-on ${addonId} to circle ${circleId}:`, error);
    throw error;
  });
};

/**
 * Remove an add-on from a user's subscription - FIXED VERSION
 * Now passes the specific stripe_subscription_id to ensure correct order is used
 * @param {string|number} circleId - Circle ID
 * @param {string|number} addonId - Add-on ID to remove
 * @param {string} token - Auth token
 * @param {string} stripeSubscriptionId - Specific subscription ID from circle
 * @param {string} orderNumber - Specific order number from circle (fallback)
 * @returns {Promise} - Promise resolving to updated subscription data
 */
const removeCircleAddon = (circleId, addonId, token, stripeSubscriptionId = null, orderNumber = null) => {
  console.log(`📡 API: Removing add-on ${addonId} from circle ${circleId} with specific identifiers`);
  console.log(`🔍 Using stripe_subscription_id: ${stripeSubscriptionId}, order_number: ${orderNumber}`);
  
  const requestData = {
    addon_id: addonId,
    action: 'remove'
  };
  
  // Include specific order identifiers to ensure backend uses the correct order
  if (stripeSubscriptionId) {
    requestData.stripe_subscription_id = stripeSubscriptionId;
  }
  if (orderNumber) {
    requestData.order_number = orderNumber;
  }
  
  return apiClient.post(`/api/circles/${circleId}/addons/`, requestData, withAuth(token))
  .then(response => {
    console.log(`✅ API: Successfully removed add-on ${addonId} from circle ${circleId}`, response);
    return response;
  })
  .catch(error => {
    console.error(`❌ API: Error removing add-on ${addonId} from circle ${circleId}:`, error);
    throw error;
  });
};

/**
 * Toggle an add-on for a circle - UPDATED VERSION
 * Now passes the specific order identifiers from the circle
 * @param {string|number} circleId - Circle ID
 * @param {string|number} addonId - Add-on ID to toggle
 * @param {boolean} enabled - Whether to enable or disable the add-on
 * @param {string} token - Auth token
 * @param {Object} circle - Full circle object with order identifiers
 * @returns {Promise} - Promise resolving to updated data
 */
const toggleCircleAddon = (circleId, addonId, enabled, token, circle = null) => {
  console.log(`🔄 Toggling add-on ${addonId} to ${enabled ? 'enabled' : 'disabled'} for circle ${circleId}`);
  
  // Extract order identifiers from circle object
  const stripeSubscriptionId = circle?.stripe_subscription_id || null;
  const orderNumber = circle?.order_number || null;
  
  console.log(`🔍 Circle identifiers - stripe_subscription_id: ${stripeSubscriptionId}, order_number: ${orderNumber}`);
  
  try {
    if (enabled) {
      console.log(`➕ Calling addCircleAddon with specific identifiers...`);
      return addCircleAddon(circleId, addonId, token, stripeSubscriptionId, orderNumber);
    } else {
      console.log(`➖ Calling removeCircleAddon with specific identifiers...`);
      return removeCircleAddon(circleId, addonId, token, stripeSubscriptionId, orderNumber);
    }
  } catch (error) {
    console.error(`❌ Error toggling add-on ${addonId} for circle ${circleId}:`, error);
    throw error;
  }
};

// ==================== MAIN SERVICE OBJECT ====================

export const circleService = {
  // ==================== CIRCLE MANAGEMENT ====================
  
  /**
   * Get all circles owned by the current user
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle data
   */
  getCircles: (token) => {
    console.log('Fetching circles owned by current user (legacy endpoint)');
    return apiClient.get('/api/circle-owners/', withAuth(token))
      .catch(error => {
        console.error('Error fetching owned circles:', error);
        throw error;
      });
  },

  /**
   * Get ONLY circles owned by the current user
   * Uses the new dedicated endpoint with proper backend filtering
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle data
   */
  getUserOwnedCircles: (token) => {
    console.log('Fetching circles owned by current user (backend filtered)');
    return apiClient.get('/api/user-owned-circles/', withAuth(token))
      .catch(error => {
        console.error('Error fetching owned circles:', error);
        throw error;
      });
  },

  /**
   * Get ONLY circles joined by the current user
   * Uses the new dedicated endpoint with proper backend filtering
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle data
   */
  getUserJoinedCircles: (token) => {
    console.log('Fetching circles joined by current user (backend filtered)');
    return apiClient.get('/api/user-joined-circles/', withAuth(token))
      .catch(error => {
        console.error('Error fetching joined circles:', error);
        throw error;
      });
  },

  /**
   * Get ALL circles for the current user (both owned and joined)
   * Uses the new dedicated endpoint with proper backend filtering
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle data
   */
  getAllUserCircles: (token) => {
    console.log('Fetching all circles for current user (backend filtered)');
    return apiClient.get('/api/user-all-circles/', withAuth(token))
      .catch(error => {
        console.error('Error fetching all user circles:', error);
        throw error;
      });
  },

  /**
   * Get details for a specific circle
   * @param {string|number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle details
   */
  getCircleDetails: (circleId, token) => {
    console.log(`Fetching circle details for: ${circleId}`);
    return apiClient.get(`/api/circle-owners/${circleId}/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching circle details for ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Get comprehensive circle management data (for CircleTabDetailView)
   * Includes plan features, pricing, and basic circle info
   * @param {number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with management data
   */
  getCircleManagementData: (circleId, token) => {
    console.log(`Fetching circle management data for: ${circleId}`);
    return apiClient.get(`/api/circles/${circleId}/management-data/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching circle management data for ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Get circle plan details (for joined circles)
   * @param {number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with plan details
   */
  getCirclePlanDetails: (circleId, token) => {
    console.log(`Fetching circle plan details for: ${circleId}`);
    return apiClient.get(`/api/circles/${circleId}/plan-details/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching circle plan details for ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Create a new circle
   * @param {Object} circleData - Circle creation data
   * @param {string} token - Auth token
   * @returns {Promise} - Response with created circle
   */
  createCircle: (circleData, token) => {
    console.log('Creating new circle:', circleData);
    return apiClient.post('/api/circle-owners/', circleData, withAuth(token))
      .catch(error => {
        console.error('Error creating circle:', error);
        throw error;
      });
  },

  /**
   * Update an existing circle
   * @param {string|number} circleId - Circle ID
   * @param {Object} circleData - Updated circle data
   * @param {string} token - Auth token
   * @returns {Promise} - Response with updated circle
   */
  updateCircle: (circleId, circleData, token) => {
    console.log(`Updating circle ${circleId}:`, circleData);
    return apiClient.put(`/api/circle-owners/${circleId}/`, circleData, withAuth(token))
      .catch(error => {
        console.error(`Error updating circle ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Delete a circle
   * @param {string|number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response confirmation
   */
  deleteCircle: (circleId, token) => {
    console.log(`Deleting circle: ${circleId}`);
    return apiClient.delete(`/api/circle-owners/${circleId}/`, withAuth(token))
      .catch(error => {
        console.error(`Error deleting circle ${circleId}:`, error);
        throw error;
      });
  },

  // ==================== CIRCLE FEATURES ====================

  /**
   * Toggle a circle feature on/off
   * @param {number} circleId - Circle ID
   * @param {number} featureId - Feature ID
   * @param {boolean} isEnabled - Whether to enable or disable the feature
   * @param {string} token - Auth token
   * @returns {Promise} - Response with updated feature data
   */
  toggleCircleFeature: (circleId, featureId, isEnabled, token) => {
    console.log(`Toggling feature ${featureId} to ${isEnabled} for circle ${circleId}`);
    return apiClient.post(
      `/api/circles/${circleId}/features/${featureId}/toggle/`, 
      { is_enabled: isEnabled },
      withAuth(token)
    )
    .catch(error => {
      console.error(`Error toggling feature ${featureId} for circle ${circleId}:`, error);
      throw error;
    });
  },

  /**
   * Send HD streaming notification email
   * @param {Object} data - Notification data
   * @param {string} token - Auth token
   * @returns {Promise} - Response
   */
  sendHDStreamingNotification: (data, token) => {
    console.log('Sending HD streaming notification:', data);
    return apiClient.post('/api/notifications/hd-streaming-toggle/', data, withAuth(token))
      .catch(error => {
        console.error('Error sending HD streaming notification:', error);
        throw error;
      });
  },

  // ==================== ADD-ON MANAGEMENT ====================

  /**
   * Get all available add-ons with optional filtering
   * @param {string} token - Auth token
   * @param {Object} filters - Optional filters (type, carrier, etc.)
   * @returns {Promise} - Promise resolving to add-ons data
   */
  getAllAddOns: (token, filters = {}) => {
    console.log('Fetching all available add-ons with filters:', filters);
    
    try {
      const queryParams = new URLSearchParams();
      
      // Add filters to query params
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.carrier) queryParams.append('carrier', filters.carrier);
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.priority) queryParams.append('priority', filters.priority);
      
      const url = `/api/addons/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      
      return apiClient.get(url, withAuth(token))
        .catch(error => {
          console.error('Error fetching add-ons:', error);
          throw error;
        });
    } catch (error) {
      console.error('Error building add-ons request:', error);
      throw error;
    }
  },

  /**
   * Get available add-ons for a specific carrier plan
   * @param {string|number} carrierPlanId - The carrier plan ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to add-ons data
   */
  getCarrierPlanAddOns: (carrierPlanId, token) => {
    console.log(`Fetching add-ons for carrier plan: ${carrierPlanId}`);
    return apiClient.get(`/api/carrier-plans/${carrierPlanId}/addons/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching add-ons for carrier plan ${carrierPlanId}:`, error);
        throw error;
      });
  },

  /**
   * Get available add-ons for a specific circle
   * This is the key method for CircleTabDetailView Add-ons tab
   * @param {string|number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to available add-ons data
   */
  getCircleAvailableAddOns: (circleId, token) => {
    console.log(`Fetching available add-ons for circle: ${circleId}`);
    return apiClient.get(`/api/circles/${circleId}/available-addons/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching available add-ons for circle ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Get user's active add-ons for a specific order/subscription
   * @param {string|number} orderId - Order ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to user's add-ons data
   */
  getUserAddOns: (orderId, token) => {
    console.log(`Fetching user add-ons for order: ${orderId}`);
    return apiClient.get(`/api/orders/${orderId}/addons/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching user add-ons for order ${orderId}:`, error);
        throw error;
      });
  },

  /**
   * Get user's active add-ons for a specific circle
   * This is the key method for CircleTabDetailView to show current add-ons
   * @param {string|number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to user's add-ons data
   */
  getUserCircleAddOns: (circleId, token) => {
    console.log(`Fetching user add-ons for circle: ${circleId}`);
    return apiClient.get(`/api/circles/${circleId}/user-addons/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching user add-ons for circle ${circleId}:`, error);
        throw error;
      });
  },

  // ==================== ADD-ON OPERATIONS (Using external functions) ====================

  /**
   * Add an add-on to a user's subscription
   */
  addCircleAddon,

  /**
   * Remove an add-on from a user's subscription
   */
  removeCircleAddon,

  /**
   * Toggle an add-on for a circle (add if not present, remove if present)
   * This is the key method for CircleTabDetailView add-on toggle functionality
   */
  toggleCircleAddon,

  /**
   * Get add-on details by ID
   * @param {string|number} addonId - Add-on ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to add-on details
   */
  getAddOnDetails: (addonId, token) => {
    console.log(`Fetching details for add-on: ${addonId}`);
    return apiClient.get(`/api/addons/${addonId}/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching add-on details for ${addonId}:`, error);
        throw error;
      });
  },

  /**
   * Cancel a user's add-on subscription
   * @param {string|number} userAddonId - UserAddOn ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to cancellation response
   */
  cancelUserAddOn: (userAddonId, token) => {
    console.log(`Cancelling user add-on subscription: ${userAddonId}`);
    return apiClient.post(`/api/user-addons/${userAddonId}/cancel/`, {}, withAuth(token))
      .catch(error => {
        console.error(`Error cancelling user add-on ${userAddonId}:`, error);
        throw error;
      });
  },

  /**
   * Reactivate a user's add-on subscription
   * @param {string|number} userAddonId - UserAddOn ID
   * @param {string} token - Auth token
   * @returns {Promise} - Promise resolving to reactivation response
   */
  reactivateUserAddOn: (userAddonId, token) => {
    console.log(`Reactivating user add-on subscription: ${userAddonId}`);
    return apiClient.post(`/api/user-addons/${userAddonId}/reactivate/`, {}, withAuth(token))
      .catch(error => {
        console.error(`Error reactivating user add-on ${userAddonId}:`, error);
        throw error;
      });
  },

  // ==================== MEMBER MANAGEMENT ====================

  /**
   * Get all members of a specific circle
   * Key method for CircleTabDetailView members list
   * @param {string|number} circleId - Circle ID
   * @param {string} token - Auth token
   * @returns {Promise} - Response with circle members
   */
  getCircleMembers: (circleId, token) => {
    console.log(`Fetching members for circle: ${circleId}`);
    return apiClient.get(`/api/circles/${circleId}/members/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching circle members for ${circleId}:`, error);
        throw error;
      });
  },

  // ==================== SHARING & SOCIAL ====================

  /**
   * Share a specific circle
   * @param {string|number} circleId - Circle ID
   * @param {Object} data - Share data
   * @param {string} token - Auth token
   * @returns {Promise} - Response confirmation
   */
  shareCircle: (circleId, data, token) => {
    console.log(`Sharing circle ${circleId} with data:`, data);
    return apiClient.post(`/api/circles/${circleId}/share/`, data, withAuth(token))
      .catch(error => {
        console.error(`Error sharing circle ${circleId}:`, error);
        throw error;
      });
  },

  /**
   * Share a circle generally (not specific circle)
   * @param {Object} data - Share data
   * @param {string} token - Auth token
   * @returns {Promise} - Response confirmation
   */
  shareCircleGeneral: (data, token) => {
    console.log('Sharing circle generally with data:', data);
    return apiClient.post('/api/circles/share/', data, withAuth(token))
      .catch(error => {
        console.error('Error sharing circle generally:', error);
        throw error;
      });
  },

  /**
   * Join a circle waitlist
   * @param {Object} data - Waitlist data
   * @param {string} token - Auth token
   * @returns {Promise} - Response confirmation
   */
  joinWaitlist: (data, token) => {
    console.log('Joining waitlist with data:', data);
    return apiClient.post('/api/waitlist/join', data, withAuth(token))
      .catch(error => {
        console.error('Error joining waitlist:', error);
        throw error;
      });
  },

  // ==================== CARRIER & PLAN MANAGEMENT ====================

  /**
   * Get circles by carrier
   * @param {string} carrierSlug - Carrier slug
   * @param {string} token - Auth token
   * @returns {Promise} - Response with carrier circles
   */
  getCirclesByCarrier: (carrierSlug, token) => {
    console.log(`Fetching circles for carrier: ${carrierSlug}`);
    return apiClient.get(`/api/carriers/${carrierSlug}/plans/`, withAuth(token))
      .catch(error => {
        console.error(`Error fetching circles for carrier ${carrierSlug}:`, error);
        throw error;
      });
  },

  // ==================== ORDER MANAGEMENT ====================

  /**
   * Update the status of an order
   * @param {string|number} orderId - The ID of the order
   * @param {Object} data - The data to update (containing subscription_status)
   * @param {string} token - Auth token
   * @returns {Promise} - API response
   */
  updateOrderStatus: (orderId, data, token) => {
    console.log(`Updating order status for order ${orderId} to ${data.subscription_status}`);
    return apiClient.patch(`/api/orders/${orderId}/status/`, data, withAuth(token))
      .catch(error => {
        console.error(`Error updating order status for ${orderId}:`, error);
        throw error;
      });
  },

  /**
   * Get the order details for the current user for a specific circle - ENHANCED VERSION
   * This function is critical for add-on operations as it provides the exact
   * stripe_subscription_id and order_number needed to identify the correct order.
   * @param {number} circleId - The ID of the circle
   * @param {string} token - Auth token
   * @returns {Promise} - Response with order details (or null if no order found)
   */
  getUserOrderForCircle: (circleId, token) => {
    console.log(`📡 API: Getting user order for circle ${circleId}`);
    
    return apiClient.get(`/api/circles/${circleId}/user-order/`, withAuth(token))
    .then(response => {
      console.log(`✅ API: User order data received for circle ${circleId}:`, response.data);
      return response;
    })
    .catch(error => {
      console.log(`ℹ️ API: No user order found for circle ${circleId}:`, error);
      // Return null instead of throwing error - this is expected if user doesn't have an order
      // This is critical for the add-on fix to work properly
      return null;
    });
  },

  // ==================== PORT & SIM MANAGEMENT ====================

  /**
   * Confirm SIM receipt - handles different identifier types
   * @param {string|number} identifier - Circle ID, subscription ID, or other identifier
   * @param {string} token - Auth token
   * @param {Object} data - Additional data including potential stripe_subscription_id
   * @returns {Promise} - API response
   */
  confirmSIMReceipt: (identifier, token, data = {}) => {
    console.log(`Confirming SIM receipt for identifier ${identifier} with data:`, data);

    // Check if we have a Stripe subscription ID in the data
    if (data.stripe_subscription_id) {
      // If we have a subscription ID in the data, use that as primary identifier
      console.log(`Using Stripe subscription ID from data: ${data.stripe_subscription_id}`);
      return apiClient.post(`/api/port/confirm-sim/${data.stripe_subscription_id}/`, data, withAuth(token))
        .catch(error => {
          console.error(`Error confirming SIM receipt using subscription ID: ${error}`);
          // If this fails, try using the provided identifier
          console.log(`Falling back to provided identifier: ${identifier}`);
          return apiClient.post(`/api/port/confirm-sim/${identifier}/`, data, withAuth(token));
        });
    }

    // If no subscription ID in data, check if identifier is a subscription ID
    if (identifier && identifier.toString().startsWith('sub_')) {
      console.log(`Using identifier as Stripe subscription ID: ${identifier}`);
      return apiClient.post(`/api/port/confirm-sim/${identifier}/`, data, withAuth(token));
    }

    // Otherwise, use circle ID as identifier
    console.log(`Using circle ID as identifier: ${identifier}`);
    return apiClient.post(`/api/port/confirm-sim/${identifier}/`, data, withAuth(token))
      .catch(error => {
        console.error(`Error confirming SIM receipt for ${identifier}:`, error);
        throw error;
      });
  },

  /**
   * Initiate port process
   * @param {string|number} identifier - Order identifier
   * @param {Object} data - Port data
   * @param {string} token - Auth token
   * @returns {Promise} - API response
   */
  initiatePort: (identifier, data, token) => {
    console.log(`Initiating port for identifier ${identifier} with data:`, data);
    return apiClient.post(`/api/orders/${identifier}/port/`, data, withAuth(token))
      .catch(error => {
        console.error(`Error initiating port for ${identifier}:`, error);
        throw error;
      });
  },

  // ==================== UTILITY METHODS ====================

  /**
   * Generic API call helper for any endpoint
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE, PATCH)
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request data (for POST/PUT/PATCH)
   * @param {string} token - Auth token
   * @returns {Promise} - API response
   */
  makeApiCall: (method, endpoint, data = null, token) => {
    console.log(`Making ${method} request to ${endpoint}`, data ? 'with data:' : '', data || '');
    
    const config = withAuth(token);
    
    switch (method.toUpperCase()) {
      case 'GET':
        return apiClient.get(endpoint, config);
      case 'POST':
        return apiClient.post(endpoint, data, config);
      case 'PUT':
        return apiClient.put(endpoint, data, config);
      case 'PATCH':
        return apiClient.patch(endpoint, data, config);
      case 'DELETE':
        return apiClient.delete(endpoint, config);
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  },

  /**
   * Batch API calls for multiple operations
   * @param {Array} calls - Array of call objects {method, endpoint, data}
   * @param {string} token - Auth token
   * @returns {Promise} - Array of API responses
   */
  makeBatchApiCalls: async (calls, token) => {
    console.log(`Making batch API calls for ${calls.length} operations`);
    
    try {
      const promises = calls.map(call => 
        circleService.makeApiCall(call.method, call.endpoint, call.data, token)
      );
      
      return await Promise.all(promises);
    } catch (error) {
      console.error('Error in batch API calls:', error);
      throw error;
    }
  }
};

export default circleService;